import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Loader2, Users, CheckCircle, CreditCard, DollarSign, TrendingUp, BarChart3, PieChart } from 'lucide-react';

const OrganizationAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600 mx-auto" />
          <p className="mt-2 text-gray-500">Loading analytics...</p>
        </CardContent>
      </Card>
    );
  }

  if (!analytics) return null;

  const { overview, trends, distributions } = analytics;

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Users</p>
                <p className="text-2xl font-bold">{overview.total_users}</p>
              </div>
              <Users className="h-8 w-8 text-indigo-600 opacity-80" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Assessments</p>
                <p className="text-2xl font-bold">{overview.total_assessed}</p>
                <p className="text-xs text-green-600">{overview.assessment_rate}% rate</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600 opacity-80" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Credits Purchased</p>
                <p className="text-2xl font-bold">{overview.total_credits_purchased}</p>
                <p className="text-xs text-gray-500">{overview.total_credits_available} available</p>
              </div>
              <CreditCard className="h-8 w-8 text-blue-600 opacity-80" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Revenue</p>
                <p className="text-2xl font-bold">${overview.total_revenue}</p>
              </div>
              <DollarSign className="h-8 w-8 text-emerald-600 opacity-80" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Assessment Trends */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Assessment Trends
          </CardTitle>
          <CardDescription>Monthly assessment completions over time</CardDescription>
        </CardHeader>
        <CardContent>
          {trends.assessments.length > 0 ? (
            <div className="space-y-4">
              {/* Simple bar chart */}
              <div className="flex items-end gap-2 h-40">
                {trends.assessments.map((item, idx) => {
                  const maxCount = Math.max(...trends.assessments.map(t => t.count));
                  const height = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center">
                      <div 
                        className="w-full bg-indigo-500 rounded-t transition-all duration-500 hover:bg-indigo-600"
                        style={{ height: `${height}%`, minHeight: item.count > 0 ? '8px' : '0' }}
                        title={`${item.count} assessments`}
                      />
                      <span className="text-xs text-gray-500 mt-2 transform -rotate-45 origin-top-left">
                        {item.month.slice(5)}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between text-sm text-gray-500 pt-4 border-t">
                <span>Monthly assessment completions</span>
                <span>Total: {trends.assessments.reduce((sum, t) => sum + t.count, 0)}</span>
              </div>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No assessment data available yet</p>
          )}
        </CardContent>
      </Card>

      {/* Signup Trends */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            User Growth
          </CardTitle>
          <CardDescription>Monthly new user registrations</CardDescription>
        </CardHeader>
        <CardContent>
          {trends.signups.length > 0 ? (
            <div className="space-y-4">
              <div className="flex items-end gap-2 h-32">
                {trends.signups.map((item, idx) => {
                  const maxCount = Math.max(...trends.signups.map(t => t.count));
                  const height = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center">
                      <div 
                        className="w-full bg-emerald-500 rounded-t transition-all duration-500 hover:bg-emerald-600"
                        style={{ height: `${height}%`, minHeight: item.count > 0 ? '8px' : '0' }}
                        title={`${item.count} signups`}
                      />
                      <span className="text-xs text-gray-500 mt-2 transform -rotate-45 origin-top-left">
                        {item.month.slice(5)}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between text-sm text-gray-500 pt-4 border-t">
                <span>Monthly new registrations</span>
                <span>Total: {trends.signups.reduce((sum, t) => sum + t.count, 0)}</span>
              </div>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No signup data available yet</p>
          )}
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Top Pairings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="h-5 w-5" />
              Top BUILDR Pairings
            </CardTitle>
            <CardDescription>Most common pairing codes in your organization</CardDescription>
          </CardHeader>
          <CardContent>
            {distributions.top_pairings.length > 0 ? (
              <div className="space-y-3">
                {distributions.top_pairings.map((item, idx) => (
                  <div key={item.pairing} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-bold">
                        {idx + 1}
                      </span>
                      <Badge variant="outline" className="text-lg font-bold px-3 py-1">
                        {item.pairing}
                      </Badge>
                    </div>
                    <span className="text-gray-600">{item.count} users</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No pairing data yet</p>
            )}
          </CardContent>
        </Card>

        {/* Role Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Role Distribution
            </CardTitle>
            <CardDescription>Breakdown of user roles</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Volunteers</span>
                  <span>{distributions.roles.volunteer || 0}</span>
                </div>
                <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-500"
                    style={{ 
                      width: `${((distributions.roles.volunteer || 0) / overview.total_users) * 100}%` 
                    }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Administrators</span>
                  <span>{distributions.roles.admin || 0}</span>
                </div>
                <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-purple-500"
                    style={{ 
                      width: `${((distributions.roles.admin || 0) / overview.total_users) * 100}%` 
                    }}
                  />
                </div>
              </div>
            </div>
            
            <div className="mt-6 pt-4 border-t">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Users with credits</span>
                <span className="font-medium">{overview.users_with_credits}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OrganizationAnalytics;
